const CONTAINER_TYPES = new Set([
  "panel",
  "fieldset",
  "well",
  "container",
  "columns",
  "table",
  "tabs",
  "datagrid",
  "editgrid",
]);

const NON_INPUT_TYPES = new Set([
  "button",
  "htmlelement",
  "content",
  "submit",
  "hidden",
]);

const EXPANDABLE_CONTAINER_TYPES = new Set(["panel", "fieldset", "well"]);

const getChildComponents = (component) => {
  if (!component) {
    return [];
  }

  if (Array.isArray(component.components)) {
    return component.components;
  }

  if (Array.isArray(component.columns)) {
    return component.columns.flatMap((column) => column.components || []);
  }

  if (Array.isArray(component.rows)) {
    return component.rows.flatMap((row) =>
      row.flatMap((cell) => cell.components || [])
    );
  }

  return [];
};

export const stripFormLabelHtml = (label) => {
  if (label == null) {
    return "";
  }

  return String(label)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const isTruthyRequiredFlag = (value) =>
  value === true || value === "true" || value === 1 || value === "1";

export const isFormComponentRequired = (component) => {
  if (!component || !isTruthyRequiredFlag(component.validate?.required)) {
    return false;
  }

  if (component.disabled === true || component.hidden === true) {
    return false;
  }

  return true;
};

export const isFormInputComponent = (component) => {
  if (!component?.key || component.input === false) {
    return false;
  }

  if (NON_INPUT_TYPES.has(component.type)) {
    return false;
  }

  return true;
};

export const isFormContainerComponent = (component) =>
  CONTAINER_TYPES.has(component?.type) ||
  Array.isArray(component?.components) ||
  Array.isArray(component?.columns) ||
  Array.isArray(component?.rows);

const getFieldValue = (data, key) => {
  if (!data || !key) {
    return undefined;
  }

  return data[key];
};

export const isFormFieldValueFilled = (value, component) => {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === "boolean") {
    return true;
  }

  if (typeof value === "number") {
    return Number.isFinite(value);
  }

  if (typeof value === "string") {
    return value.trim() !== "";
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return false;
    }

    if (component?.type === "file") {
      return value.some(
        (entry) =>
          entry &&
          (entry.url ||
            entry.name ||
            entry.originalName ||
            entry.storage ||
            entry.size)
      );
    }

    return value.length > 0;
  }

  if (typeof value === "object") {
    if (component?.type === "selectboxes") {
      return Object.values(value).some((entry) => entry === true);
    }

    if (component?.type === "file") {
      return Boolean(value.url || value.name || value.originalName);
    }

    return Object.keys(value).length > 0;
  }

  return true;
};

export const isFormComponentVisible = (component, data) => {
  const conditional = component?.conditional;

  if (!conditional) {
    return true;
  }

  if (conditional.json) {
    return true;
  }

  const actualValue = getFieldValue(data, conditional.when);
  const showWhenMatched = conditional.show !== false;

  if (conditional.eq !== undefined) {
    const matches = String(actualValue) === String(conditional.eq);
    return showWhenMatched ? matches : !matches;
  }

  if (conditional.neq !== undefined) {
    const matches = String(actualValue) !== String(conditional.neq);
    return showWhenMatched ? matches : !matches;
  }

  return true;
};

const buildMissingFieldEntry = (component, componentInstance = null) => ({
  key: component.key,
  label: stripFormLabelHtml(component.label) || component.key,
  componentInstance,
});

const isFormioInputMissing = (componentInstance) => {
  const schema = componentInstance?.component;

  if (!schema?.key || componentInstance.noField) {
    return false;
  }

  if (!componentInstance.hasInput || NON_INPUT_TYPES.has(schema.type)) {
    return false;
  }

  if (schema.disabled || schema.hidden) {
    return false;
  }

  if (!isTruthyRequiredFlag(schema.validate?.required)) {
    return false;
  }

  if (!componentInstance.visible || componentInstance.parentVisible === false) {
    return false;
  }

  return componentInstance.isEmpty();
};

export const getMissingRequiredFieldsFromFormio = (formio) => {
  if (!formio?.everyComponent) {
    return [];
  }

  const missingFields = [];
  const seenKeys = new Set();

  formio.everyComponent((componentInstance) => {
    if (!isFormioInputMissing(componentInstance)) {
      return;
    }

    const { key } = componentInstance.component;
    if (seenKeys.has(key)) {
      return;
    }

    seenKeys.add(key);
    missingFields.push(
      buildMissingFieldEntry(componentInstance.component, componentInstance)
    );
  });

  return missingFields;
};

export const getMissingRequiredFields = (
  components,
  data,
  { parentVisible = true } = {}
) => {
  const missingFields = [];
  const seenKeys = new Set();

  const visit = (items, visible) => {
    items?.forEach((component) => {
      const isVisible =
        visible && isFormComponentVisible(component, data || {});

      if (isFormContainerComponent(component)) {
        visit(getChildComponents(component), isVisible);
        return;
      }

      if (
        !isVisible ||
        !isFormInputComponent(component) ||
        !isFormComponentRequired(component) ||
        seenKeys.has(component.key)
      ) {
        return;
      }

      const value = getFieldValue(data, component.key);
      if (!isFormFieldValueFilled(value, component)) {
        seenKeys.add(component.key);
        missingFields.push(buildMissingFieldEntry(component));
      }
    });
  };

  visit(components, parentVisible);
  return missingFields;
};

export const resolveMissingRequiredFields = ({
  formRef,
  components,
  data,
} = {}) => {
  const formio = formRef?.current?.formio;

  if (formio) {
    return getMissingRequiredFieldsFromFormio(formio);
  }

  return getMissingRequiredFields(components, data);
};

const expandFormioContainer = (container) => {
  if (!container?.component) {
    return;
  }

  const { type } = container.component;

  if (
    EXPANDABLE_CONTAINER_TYPES.has(type) &&
    container.component.collapsible !== false &&
    container.collapsed
  ) {
    container.collapsed = false;
    return;
  }

  if (type === "tabs" && typeof container.setTab === "function") {
    const tabIndex =
      typeof container.currentTab === "number" ? container.currentTab : 0;
    container.setTab(tabIndex);
  }
};

export const expandFormioAncestors = (componentInstance) => {
  if (!componentInstance) {
    return;
  }

  const ancestors = [];
  let parent = componentInstance.parent;

  while (parent) {
    ancestors.push(parent);
    parent = parent.parent;
  }

  ancestors.reverse().forEach((ancestor) => {
    if (
      ancestor.component?.type === "tabs" &&
      typeof ancestor.setTab === "function"
    ) {
      let node = componentInstance;
      while (node && node.parent !== ancestor) {
        node = node.parent;
      }

      const tabIndex =
        typeof node?.tab === "number" ? node.tab : ancestor.currentTab;

      if (typeof tabIndex === "number") {
        ancestor.setTab(tabIndex);
      }
      return;
    }

    expandFormioContainer(ancestor);
  });
};

const expandCollapsedSectionsInDom = (fieldElement) => {
  if (!fieldElement) {
    return;
  }

  let node = fieldElement;

  while (node) {
    const panel = node.classList?.contains("formio-component-panel")
      ? node
      : node.closest?.(".formio-component-panel");

    if (panel) {
      const collapseTarget =
        panel.querySelector(".card-body") ||
        panel.querySelector(".panel-body") ||
        panel.querySelector("[ref='nested-panel']");

      const isHidden =
        collapseTarget &&
        (collapseTarget.offsetParent === null ||
          collapseTarget.classList.contains("hidden") ||
          collapseTarget.style.display === "none");

      if (isHidden || panel.classList.contains("collapsed")) {
        const toggle =
          panel.querySelector(".card-header") ||
          panel.querySelector(".panel-heading") ||
          panel.querySelector("[ref='header']") ||
          panel.querySelector(".mb-0");

        toggle?.click();
      }
    }

    const tabPane = node.closest?.(".tab-pane");
    if (tabPane && !tabPane.classList.contains("active")) {
      const tabId = tabPane.getAttribute("id");
      const tabTrigger = tabId
        ? document.querySelector(`[href="#${tabId}"], [data-target="#${tabId}"]`)
        : null;
      tabTrigger?.click();
    }

    node = node.parentElement;
  }
};

const highlightFormFieldElement = (target) => {
  if (!target) {
    return;
  }

  target.classList.add("form-required-field-highlight");
  window.setTimeout(() => {
    target.classList.remove("form-required-field-highlight");
  }, 2000);
};

const focusFormFieldElement = (target) => {
  const focusable = target.querySelector(
    "input:not([type='hidden']), textarea, select, button, [tabindex]:not([tabindex='-1'])"
  );
  focusable?.focus({ preventScroll: true });
};

export const scrollToFormField = (key, element) => {
  if (!key && !element) {
    return;
  }

  const target =
    element ||
    document.querySelector(`.formio-component-${key}`) ||
    document.getElementById(key);

  if (!target) {
    return;
  }

  expandCollapsedSectionsInDom(target);
  target.scrollIntoView({ behavior: "smooth", block: "center" });
  focusFormFieldElement(target);
  highlightFormFieldElement(target);
};

export const revealAndScrollToFormField = ({
  formRef,
  field,
  scrollDelayMs = 150,
} = {}) => {
  if (!field?.key) {
    return;
  }

  const formio = formRef?.current?.formio;
  const componentInstance =
    field.componentInstance ||
    (formio?.getComponent ? formio.getComponent(field.key) : null);

  if (componentInstance) {
    expandFormioAncestors(componentInstance);
  }

  const performScroll = () => {
    const element =
      componentInstance?.element ||
      document.querySelector(`.formio-component-${field.key}`) ||
      document.getElementById(field.key);

    if (element) {
      expandCollapsedSectionsInDom(element);
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      focusFormFieldElement(element);
      highlightFormFieldElement(element);
      return;
    }

    scrollToFormField(field.key);
  };

  window.setTimeout(performScroll, scrollDelayMs);
};

export const buildMissingFieldsTooltip = (missingFields) => {
  if (!missingFields?.length) {
    return "";
  }

  return missingFields.map((field) => field.label).join("\n");
};
